import { useState, useEffect, useRef } from "react";
import type { Theme, FontPairing, Workflow } from "@structurify/shared-types";
import { MonoLabel } from "../components/primitives/index.ts";

interface Props {
  theme: Theme;
  fonts: FontPairing;
  workflow?: Workflow | undefined;
  onUpload: (file: File) => void;
  onBulk: () => void;
  onScan: () => void;
  onDemo?: () => void;
}

export function StageUpload({ theme, fonts, workflow, onUpload, onBulk, onScan, onDemo }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 720);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 720);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  function pickFile(file: File) {
    onUpload(file);
  }

  return (
    <div
      style={{
        flex: 1,
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "clamp(24px, 5vw, 48px) clamp(20px, 5vw, 48px) clamp(20px, 4vw, 36px)",
      }}
    >
      {/* Main content — vertically centered in the remaining space above the footer */}
      <div style={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: 640, textAlign: "center", opacity: 0, animation: "fadeUp 700ms 100ms forwards ease-out" }}>
          <MonoLabel theme={theme} fonts={fonts} size={10} style={{ display: "block", marginBottom: "clamp(16px, 3vw, 28px)" }}>
            {workflow ? `WORKFLOW · ${workflow.name.toUpperCase()}` : "NEW DOCUMENT · UNTITLED"}
          </MonoLabel>
          <h1
            style={{
              fontFamily: fonts.headerFamily === "serif" ? fonts.serif : fonts.sans,
              fontWeight: fonts.headerWeight,
              fontSize: "clamp(34px, 7vw, 56px)",
              lineHeight: 1.05,
              letterSpacing: fonts.headerTracking,
              color: theme.ink,
              margin: 0,
            }}
          >
            Upload a document.
          </h1>
          <p
            style={{
              fontFamily: fonts.sans,
              fontSize: "clamp(14px, 1.6vw, 16px)",
              lineHeight: 1.5,
              color: theme.inkSoft,
              marginTop: 14,
              marginBottom: 0,
              fontWeight: 400,
            }}
          >
            Structurify will read it, find the values that matter,
            <br />
            and show you exactly where each one came from.
          </p>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) pickFile(file);
          }}
          onClick={() => fileInputRef.current?.click()}
          style={{
            marginTop: "clamp(24px, 4vw, 48px)",
            width: "100%",
            maxWidth: 640,
            border: `1px ${dragOver ? "solid" : "dashed"} ${dragOver ? theme.ink : theme.ruleStrong}`,
            background: dragOver ? theme.accentSoft : "transparent",
            borderRadius: 3,
            padding: "clamp(32px, 5vw, 56px) clamp(20px, 4vw, 40px)",
            cursor: "pointer",
            transition: "all 220ms ease",
            opacity: 0,
            animation: "fadeUp 700ms 220ms forwards ease-out",
            textAlign: "center",
          }}
        >
          <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 16, opacity: 0.85 }}>
            <svg width="22" height="28" viewBox="0 0 22 28">
              <path d="M2 1.5 L14 1.5 L20 7.5 L20 26.5 L2 26.5 Z" fill="none" stroke={theme.ink} strokeWidth="1" />
              <path d="M14 1.5 L14 7.5 L20 7.5" fill="none" stroke={theme.ink} strokeWidth="1" />
              <line x1="6" y1="13" x2="16" y2="13" stroke={theme.inkMute} strokeWidth="0.75" />
              <line x1="6" y1="16" x2="16" y2="16" stroke={theme.inkMute} strokeWidth="0.75" />
              <line x1="6" y1="19" x2="13" y2="19" stroke={theme.inkMute} strokeWidth="0.75" />
            </svg>
          </div>
          <div style={{ fontFamily: fonts.sans, fontSize: 15, color: theme.ink, fontWeight: 500 }}>
            {isMobile ? "Tap to choose a document" : "Drop a document here, or click to browse"}
          </div>
          <div style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute, marginTop: 10, letterSpacing: "0.06em" }}>
            PDF · DOCX · PNG · JPG · TIFF · HEIC · scans up to 50MB
          </div>
        </div>

        <div
          style={{
            marginTop: 16,
            display: "flex",
            gap: 18,
            flexWrap: "wrap",
            justifyContent: "center",
            opacity: 0,
            animation: "fadeUp 700ms 320ms forwards ease-out",
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); onScan(); }}
            style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.inkSoft, background: "transparent", border: "none", cursor: "pointer", padding: "6px 4px", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13">
              <rect x="1.5" y="3" width="10" height="7.5" fill="none" stroke="currentColor" strokeWidth="1" />
              <circle cx="6.5" cy="6.75" r="1.75" fill="none" stroke="currentColor" strokeWidth="1" />
              <path d="M4 3 L4.6 1.5 L8.4 1.5 L9 3" fill="none" stroke="currentColor" strokeWidth="1" />
            </svg>
            Scan with camera →
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onBulk(); }}
            style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.inkSoft, background: "transparent", border: "none", cursor: "pointer", padding: "6px 4px" }}
          >
            or process many at once →
          </button>
          {onDemo && (
            <button
              onClick={(e) => { e.stopPropagation(); onDemo(); }}
              style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.inkMute, background: "transparent", border: "none", cursor: "pointer", padding: "6px 4px", textDecoration: "underline", textUnderlineOffset: 3 }}
            >
              Try a demo document →
            </button>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.png,.jpg,.jpeg,.tiff,.tif,.heic"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) pickFile(file);
          e.target.value = "";
        }}
      />

      {/* Footer trust signals — in normal flow, always below the content */}
      <div
        style={{
          marginTop: "clamp(24px, 4vw, 36px)",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: "clamp(12px, 3vw, 28px)",
          flexWrap: "wrap",
          opacity: 0,
          animation: "fadeUp 700ms 380ms forwards ease-out",
        }}
      >
        {["Encrypted in transit & at rest", "Documents never train models", "SOC 2 Type II"].map((text, i) => (
          <div
            key={i}
            style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute, letterSpacing: "0.06em" }}
          >
            <span style={{ width: 4, height: 4, borderRadius: 4, background: theme.inkMute, opacity: 0.6 }} />
            {text}
          </div>
        ))}
      </div>
    </div>
  );
}
